import {PHASES} from "../../../constants/phases";
import {POLICIES} from "../../../constants/policies";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {isPlayerNextPolicy} from "../../factions/isPlayerNextPolicy";
import {checkIfHasRemainingConstructionActions} from "../construction/checkIfHasRemainingConstructionActions";

/**
 * Причины, по которым правила не дают перехватить инициативу. Ими пользуются
 * и редьюсер (глушит экшен), и сервер (не применяет и не пишет в запись), и
 * MCP-инструмент (объясняет агенту отказ)
 */
export const INITIATIVE_INTERCEPTION_REJECTIONS = {
  WRONG_PHASE: 'WRONG_PHASE',
  ALREADY_INITIATIVE_OWNER: 'ALREADY_INITIATIVE_OWNER',
  OTHER_PLAYER_INTERCEPTED: 'OTHER_PLAYER_INTERCEPTED',
  ALREADY_INTERCEPTED_BY_ME: 'ALREADY_INTERCEPTED_BY_ME',
  NO_CONSTRUCTION_ACTIONS: 'NO_CONSTRUCTION_ACTIONS',
  OWNER_HAS_ISOLATIONISM: 'OWNER_HAS_ISOLATIONISM',
  NO_ISOLATIONISM_POLICY: 'NO_ISOLATIONISM_POLICY',
};

const INITIATIVE_INTERCEPTION_ACTION_TYPES = {
  TOGGLE: 'TOGGLE_INITIATIVE_INTERCEPTION',
  ISOLATIONISM: 'POLICY_ISOLATIONISM_INTERCEPT_INITIATIVE',
};

/**
 * Почему игроку нельзя ВЗЯТЬ инициативу (null — можно).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. Правило перехвата до сих пор жило только в кнопке
 * (InitiativeInterceptionButton) и у ботов (decideInitiativeStep), а движок
 * знал ровно одно ограничение — «перехватил кто-то другой». Клиент, который
 * шлет экшен мимо кнопки (MCP-агент через toggle_initiative_interception или
 * dispatch_raw_action), обходил все остальные правила: в матче iwvn0wn игрок
 * семь раундов подряд «перехватывал» инициативу, которой и так владел, и этим
 * занимал слот перехвата раунда — соперник перехватить уже не мог.
 *
 * ВЛАДЕЛЕЦ ИНИЦИАТИВЫ — ЭТО turnOrder[0], а не nextInitiativePlayerId. Порядок
 * хода перестраивается только на смене фазы (START_NEXT_PHASE), поэтому внутри
 * фазы Строительства перехватчик остается НЕ первым: состояния «я владею
 * инициативой» и «я держу свой перехват» не пересекаются.
 *
 * ОТМЕНА ПЕРЕХВАТА СЮДА НЕ ХОДИТ: отменить свой перехват можно всегда (отмена
 * возвращает потраченное действие, поэтому проверять остаток действий на ней
 * нельзя — игрок запер бы сам себя). Разбирает это getInitiativeActionRejection.
 *
 * @param {Object} gameState — общее состояние партии (shared.gameState)
 * @param {Object} player — игрок, который пытается перехватить
 * @param {boolean} [options.isFreeIsolationismInterception] — бесплатный
 *        перехват при покупке Изоляционизма: он не тратит действие, но требует
 *        самой политики и свободного слота перехвата
 * @returns {string|null} причина из INITIATIVE_INTERCEPTION_REJECTIONS
 */
export const getInitiativeInterceptionRejection = (gameState, player, options = {}) => {
  const {isFreeIsolationismInterception = false} = options;

  // Не за кого или не в чем решать — правило молчит (пустое состояние в тестах,
  // сборка стора, состояние до START_GAME)
  if (!gameState || !player) {
    return null;
  }

  if (gameState.phase !== PHASES.CONSTRUCTION) {
    return INITIATIVE_INTERCEPTION_REJECTIONS.WRONG_PHASE;
  }

  const initiativeOwnerId = gameState.turnOrder?.[0];
  if (initiativeOwnerId && initiativeOwnerId === player.id) {
    return INITIATIVE_INTERCEPTION_REJECTIONS.ALREADY_INITIATIVE_OWNER;
  }

  // Перехват в раунде один на всех: занявший слот его и держит
  if (gameState.nextInitiativePlayerId && gameState.nextInitiativePlayerId !== player.id) {
    return INITIATIVE_INTERCEPTION_REJECTIONS.OTHER_PLAYER_INTERCEPTED;
  }

  if (isFreeIsolationismInterception) {
    // Бесплатный перехват предлагается только в момент покупки Изоляционизма и
    // только на свободный слот: иначе им можно было бы «переоформить» уже
    // сделанный платный перехват и вернуть себе потраченное действие
    if (gameState.nextInitiativePlayerId === player.id) {
      return INITIATIVE_INTERCEPTION_REJECTIONS.ALREADY_INTERCEPTED_BY_ME;
    }
    if (!isPlayerPolicy(player, POLICIES.ISOLATIONISM) && !isPlayerNextPolicy(player, POLICIES.ISOLATIONISM)) {
      return INITIATIVE_INTERCEPTION_REJECTIONS.NO_ISOLATIONISM_POLICY;
    }
  } else if (!checkIfHasRemainingConstructionActions(
    player,
    gameState.pendingBuildings || [],
    gameState.nextInitiativePlayerId,
    gameState.isInitiativeInterceptedWithIsolationism
  )) {
    return INITIATIVE_INTERCEPTION_REJECTIONS.NO_CONSTRUCTION_ACTIONS;
  }

  // Изоляционизм защищает право первого хода своего владельца сразу после
  // покупки, поэтому смотрим и текущую политику, и следующую
  const initiativeOwner = (gameState.players || []).find((item) => item.id === initiativeOwnerId);
  if (initiativeOwner
    && (isPlayerPolicy(initiativeOwner, POLICIES.ISOLATIONISM)
      || isPlayerNextPolicy(initiativeOwner, POLICIES.ISOLATIONISM))
  ) {
    return INITIATIVE_INTERCEPTION_REJECTIONS.OWNER_HAS_ISOLATIONISM;
  }

  return null;
};

/**
 * Почему правила не дают применить пришедший экшен инициативы (null — можно).
 * Автор экшена — ходящий игрок: у самого экшена автора нет, а в старых записях
 * нет и payload (см. actionsLegacyPayload.test.js).
 *
 * Одной функцией пользуются редьюсер, серверная ретрансляция и MCP-инструмент,
 * чтобы проверки не разъехались: инструмент обязан отказывать ровно там, где
 * откажет движок.
 */
export const getInitiativeActionRejection = (gameState, action) => {
  const actionType = action?.type;
  if (!gameState || !actionType) {
    return null;
  }
  const currentPlayer = gameState.players?.[gameState.currentPlayerIndex];
  if (!currentPlayer) {
    return null;
  }

  if (actionType === INITIATIVE_INTERCEPTION_ACTION_TYPES.TOGGLE) {
    // Свой перехват отменяем без условий
    if (gameState.nextInitiativePlayerId === currentPlayer.id) {
      return null;
    }
    return getInitiativeInterceptionRejection(gameState, currentPlayer);
  }

  if (actionType === INITIATIVE_INTERCEPTION_ACTION_TYPES.ISOLATIONISM) {
    return getInitiativeInterceptionRejection(gameState, currentPlayer, {
      isFreeIsolationismInterception: true,
    });
  }

  return null;
};
